import { useState, useEffect, useRef } from 'react';
import { Check, Pencil } from 'lucide-react';
import RichTextEditor from '../RichTextEditor';

export interface Section6Data {
  principlesText: string;
  confirmed: boolean;
}

// Bereits in der Form, die Tiptap beim Parsen/Serialisieren selbst erzeugt (keine Leerzeilen
// zwischen Blockelementen, <li> umschließt seinen Text mit <p>) — sonst meldet der Editor
// gleich beim ersten Rendern eine "Änderung" (seine eigene Normalisierung des Default-Texts),
// die über den Eltern-Kreislauf zurückgereicht wird und dort wieder als extern gesetzter
// Wert ankommt; da der zurückgereichte Text nicht mehr mit der rohen Vorlage übereinstimmt,
// schaukelt sich das zwischen beiden Formen endlos hoch (gefunden beim Entwickeln von
// "Grundsätze"-Abschnitt opt-in, 2026-10-12 — Endlosschleife/Browser-Hänger sobald der
// Abschnitt nach dem Verstecken wieder aktiv wird).
const DEFAULT_PRINCIPLES_HTML = `<h2>6. Berücksichtigung der bereichsübergreifenden Grundsätze der Gleichstellung, Antidiskriminierung und ökologischen Nachhaltigkeit</h2><p>Im Rahmen der durchgeführten Beratung wurden die bereichsübergreifenden Grundsätze der Gleichstellung der Geschlechter, Antidiskriminierung und ökologischen Nachhaltigkeit mitberücksichtigt. Auch wenn diese Themen nicht explizit als Beratungsschwerpunkte definiert wurden, wurden sie in den Gesprächen und Empfehlungen integriert, um nachhaltige und diskriminierungsfreie Unternehmensprozesse zu fördern.</p><h3>6.1 Ökologische Nachhaltigkeit</h3><p>Die Beratung erfolgte unter besonderer Berücksichtigung nachhaltiger Aspekte, um Ressourcen zu schonen und umweltfreundliche Alternativen aufzuzeigen. Dabei wurden insbesondere folgende Maßnahmen thematisiert und empfohlen:</p><ul><li><p><strong>Digitale Dokumentation und papierlose Kommunikation:</strong> Es wurde empfohlen, den Papierverbrauch durch verstärkte Nutzung digitaler Dokumentationen zu reduzieren. Zudem wurde angeregt, in E-Mail-Signaturen einen Hinweis wie „Bitte prüfen Sie, ob das Ausdrucken dieser E-Mail wirklich notwendig ist." aufzunehmen.</p></li><li><p><strong>Reduzierung von Druckmaterialien:</strong> Im Zuge der Beratung über digitale Präsenz wurde darauf hingewiesen, möglichst auf Plakate und gedruckte Informationsmaterialien zu verzichten und stattdessen digitale Alternativen zu nutzen.</p></li><li><p><strong>Verwendung nachhaltiger Materialien:</strong> Falls Printmaterialien unvermeidbar sind, wurde die Nutzung von Recyclingpapier als umweltfreundliche Alternative empfohlen.</p></li><li><p><strong>Energieeffiziente Kommunikation:</strong> Beratungen wurden, wo möglich, per Videokonferenz durchgeführt, um Fahrwege zu reduzieren und die CO₂-Bilanz zu verbessern. Falls eine Anreise notwendig war, wurde darauf geachtet, Fahrgemeinschaften zu bilden, um den Kraftstoffverbrauch zu senken.</p></li></ul><h3>6.2 Gleichstellung der Geschlechter und Antidiskriminierung</h3><p>Auch wenn das Thema Gleichstellung nicht im Mittelpunkt der Beratung stand, wurde darauf geachtet, dass in den Unternehmensprozessen und der Kundenkommunikation keine geschlechtsbezogenen Nachteile entstehen. Folgende Aspekte wurden in die Beratung einbezogen:</p><ul><li><p><strong>Vermeidung geschlechtsbezogener Vorurteile:</strong> In allen Beratungs- und Entscheidungsprozessen wurde darauf hingewiesen, dass keine unbewussten Vorurteile entstehen oder weitergetragen werden.</p></li><li><p><strong>Geschlechtersensible Gestaltung von Dokumentationen und Prozessen:</strong> Die Bedeutung neutraler und inklusiver Sprache in internen sowie externen Kommunikationsmitteln wurde angesprochen.</p></li><li><p><strong>Bewusstsein für geschlechtsspezifische Vorurteile:</strong> Der Einfluss unbewusster Vorannahmen auf die Entscheidungsfindung und Kundenansprache wurde diskutiert.</p></li><li><p><strong>Vermeidung von stereotypen oder diskriminierenden Darstellungen:</strong> Es wurde empfohlen, sicherzustellen, dass Unternehmenskommunikation keine geschlechtsbezogenen Klischees bedient und alle Zielgruppen gleichermaßen anspricht.</p></li></ul><p>Diese Aspekte wurden als integraler Bestandteil der Beratung behandelt, um sicherzustellen, dass Unternehmensentscheidungen diskriminierungsfrei getroffen werden und Nachhaltigkeitsziele unterstützt werden.</p>`;

interface Section6PrinciplesProps {
  darkMode: boolean;
  onDataChange: (data: Section6Data) => void;
  onGenerate: () => void;
  initialData?: Section6Data;
}

export function Section6Principles({
  darkMode,
  onDataChange,
  onGenerate,
  initialData,
}: Section6PrinciplesProps) {
  const [principlesText, setPrinciplesText] = useState(
    initialData?.principlesText || DEFAULT_PRINCIPLES_HTML
  );
  const [confirmed, setConfirmed] = useState(!!initialData?.confirmed);
  const editorContainerRef = useRef<HTMLDivElement>(null);

  const onDataChangeRef = useRef(onDataChange);
  onDataChangeRef.current = onDataChange;

  useEffect(() => {
    onDataChangeRef.current({ principlesText, confirmed });
  }, [principlesText, confirmed]);

  /**
   * Der Bericht wird asynchron geladen — beim Mounten steht hier noch der Standard.
   *
   * Ohne diesen Abgleich bleibt die Bestätigung des Nutzers unsichtbar: Der gespeicherte
   * Haken trifft erst NACH dem Mounten ein, der Abschnitt behielt aber seinen Startwert
   * („nicht bestätigt") und meldete ihn per onDataChange nach oben — der geladene Stand war
   * damit wieder weg, und der Fortschritt stand auf 0%, obwohl in der Datenbank
   * `confirmed: true` lag.
   *
   * Übernommen wird nur, was sich von außen WIRKLICH ändert (Vergleich gegen den zuletzt
   * gesehenen Wert). Sonst würde das eigene Zurücksetzen beim Tippen sofort wieder
   * überschrieben.
   */
  const zuletztVonAussen = useRef({ text: initialData?.principlesText || '', confirmed: !!initialData?.confirmed });
  /** Zuletzt VON AUSSEN gesetzter Text. Der Editor meldet auch programmatische Änderungen
   *  als onChange — ohne diese Merke würde das Nachladen die Bestätigung sofort wieder
   *  entwerten (siehe Textänderung weiter unten). */
  const vonAussenGesetzt = useRef<string | null>(null);
  useEffect(() => {
    const text = initialData?.principlesText || '';
    const bestaetigt = !!initialData?.confirmed;
    if (text !== zuletztVonAussen.current.text) {
      zuletztVonAussen.current.text = text;
      if (text) { vonAussenGesetzt.current = text; setPrinciplesText(text); }
    }
    if (bestaetigt !== zuletztVonAussen.current.confirmed) {
      zuletztVonAussen.current.confirmed = bestaetigt;
      setConfirmed(bestaetigt);
    }
  }, [initialData?.principlesText, initialData?.confirmed]);

  // Eine Änderung am Text entwertet eine vorherige Bestätigung — die bezog
  // sich auf den alten Wortlaut.
  const handleTextChange = (html: string) => {
    setPrinciplesText(html);
    // Nur eine Änderung des Nutzers entwertet die Bestätigung. Der nachgeladene Text ist
    // genau der, der bestätigt wurde — er darf den Haken nicht zurücknehmen.
    if (html === vonAussenGesetzt.current) return;
    setConfirmed(false);
  };

  const handleEditClick = () => {
    const editable = editorContainerRef.current?.querySelector<HTMLElement>('.ProseMirror');
    if (!editable) return;
    editable.focus();
    editable.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  return (
    <div className={`rounded-lg shadow-sm flex-1 ${darkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} border`}>
      <div className="mx-6 my-6">
        {/* Hinweis */}
        <div className={`mb-5 p-3 rounded-lg text-sm ${
          darkMode ? 'bg-green-900/20 border-green-800 text-green-300' : 'bg-green-50 border-green-200 text-green-700'
        } border flex items-center gap-2`}>
          <Check className="w-4 h-4 flex-shrink-0" />
          <span>Standardtext wird automatisch eingefügt. Sie können ihn direkt bearbeiten und formatieren.</span>
        </div>

        {/* TipTap Editor */}
        <div ref={editorContainerRef}>
          <RichTextEditor
            content={principlesText}
            onChange={handleTextChange}
            darkMode={darkMode}
            placeholder="Text eingeben..."
            minHeight="400px"
            baseFontSize="14px"
          />
        </div>

        {/* Kein "Text generieren" Button — stattdessen explizite Bestätigung */}
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <p className={`text-xs ${darkMode ? 'text-slate-500' : 'text-slate-400'}`}>
            Dieser Abschnitt benötigt keine KI-Generierung
          </p>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleEditClick}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg border text-sm font-medium shadow-sm transition-colors ${
                darkMode
                  ? 'bg-slate-700 text-slate-200 border-slate-600 hover:bg-slate-600'
                  : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
              }`}
            >
              <Pencil className="w-4 h-4" />
              Bearbeiten
            </button>
            <button
              type="button"
              onClick={() => setConfirmed(true)}
              disabled={confirmed}
              className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium shadow-sm transition-colors text-white ${
                confirmed ? 'bg-emerald-600/70 cursor-default' : 'bg-emerald-600 hover:bg-emerald-700'
              }`}
            >
              <Check className="w-4 h-4" />
              {confirmed ? 'Bestätigt' : 'Bestätigen'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
